import React, { useEffect, useMemo, useState } from "react";
import { observer, inject } from "mobx-react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import TableHeader from "./TableHeader";
import TableRow from "./TableRow";
import { Pagination, Stack } from "@mui/material";
import { Link } from "react-router-dom";
import { getSavedSort, saveSort } from "../utils/storage";

const rowHoverStyle = {
  cursor: "pointer",

  "&:hover *": {
    fontWeight: "bolder",
    cursor: "pointer",
    userSelect: "none",
  },
};

export const RowLink = styled(Link)(() => rowHoverStyle);

// Same look as RowLink, for clickable rows that open something in place instead of navigating
const ClickableRow = styled.div(() => rowHoverStyle);

// Sorts a copy of `items` by { key, direction } (dates compared as dates, nulls last).
// Shared with lists that have no table header, e.g. the mobile task lists.
export const sortItems = (items, sortConfig) => {
  const sortableItems = [...(items || [])];
  if (!sortConfig?.key) return sortableItems;

  const { key, direction } = sortConfig;
  const ascending = direction === "ascending";

  if (key.includes("DATE")) {
    sortableItems.sort((a, b) => {
      if (new Date(a[key]) <= new Date(b[key])) {
        return ascending ? -1 : 1;
      }
      if (new Date(a[key]) > new Date(b[key])) {
        return ascending ? 1 : -1;
      }
      return 0;
    });
  } else {
    sortableItems.sort((a, b) => {
      if (a[key] === null && b[key] === null) {
        return 0;
      }
      if (a[key] === null && b[key] !== null) {
        return ascending ? 1 : -1;
      }
      if (a[key] !== null && b[key] === null) {
        return ascending ? -1 : 1;
      }

      if (a[key] < b[key]) {
        return ascending ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return ascending ? 1 : -1;
      }
      return 0;
    });
  }
  return sortableItems;
};

// sortStorageKey: when given, the user's sort choice is remembered and used next time
// (instead of `config`, which is only the default)
const useSortableData = (items, config, sortStorageKey) => {
  const [sortConfig, setSortConfig] = React.useState(
    () => getSavedSort(sortStorageKey) || config
  );
  const sortedItems = useMemo(() => sortItems(items, sortConfig), [items, sortConfig]);

  const requestSort = (key) => {
    let direction = "descending";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "descending"
    ) {
      direction = "ascending";
    }
    const newSortConfig = { key, direction };
    setSortConfig(newSortConfig);
    saveSort(sortStorageKey, newSortConfig);
  };
  return { sortedItems, requestSort, sortConfig };
};

const Table = ({
  tab,
  keys,
  items,
  translation,
  config,
  hasDelete,
  handelRowUpdateClick,
  handelRowDeleteClick,
  handelRowCancel,
  withIndex,
  noPagination,
  editable,
  onClick,
  headerHeight = 0,
  isRtl,
  handelRowClick = () => {},
  linkTo = () => {},
  clickable = true,
  style,
  fromTask = false,
  sortStorageKey,
}) => {
  const { t } = useTranslation(translation);
  const theme = useTheme();

  const { sortedItems, requestSort, sortConfig } = useSortableData(
    items,
    { ...config },
    sortStorageKey
  );

  const [page, setPage] = useState(1);
  const rownum = Math.floor(
    (document.body.offsetHeight - 400 - headerHeight) / 50
  );

  useEffect(() => {
    setPage(1);
  }, [items, sortedItems]);

  const handleChange = (event, value) => {
    setPage(value);
  };

  return (
    <div>
      {sortedItems?.length === 0 && (
        <div
          style={{
            justifyContent: "center",
            color: theme.color.text,
            fontSize: "21px",
            margin: "10px 50px",
          }}
        >
          {t("no_items")}
        </div>
      )}
      {sortedItems?.length > 0 && (
        <div>
          {!noPagination && sortedItems.length > rownum && (
            <Stack
              spacing={2}
              style={{
                alignItems: "end",
                marginTop: !fromTask ? 20 : "unset",
                marginBottom: 20,
                marginInlineEnd: !fromTask ? 42 : "unset",
              }}
            >
              <Pagination
                count={
                  Math.floor(sortedItems.length % rownum) !== 0
                    ? Math.floor(sortedItems.length / rownum) + 1
                    : Math.floor(sortedItems.length / rownum) + 0
                }
                sx={{
                  direction: "ltr",
                }}
                shape="rounded"
                page={page}
                onChange={handleChange}
              />
            </Stack>
          )}
          <TableHeader
            keys={keys}
            items={items}
            handelSort={requestSort}
            translation={translation}
            config={sortConfig}
            hasDelete={hasDelete}
            withIndex={withIndex}
            editable={editable}
            fromTask={fromTask}
          />
          <div
            style={{
              overflowY: noPagination === true ? "auto" : "none",
              resize: "vertical",
              height: noPagination === true ? `60vh` : "auto",
              ...style,
            }}
          >
            {(noPagination === true
              ? sortedItems
              : sortedItems.slice((page - 1) * rownum, page * rownum)
            ).map((item, index) => {
              const to = linkTo(item);
              const row = (
                <TableRow
                  keys={keys}
                  index={index + (page - 1) * rownum}
                  tab={tab}
                  row={item}
                  isRtl={isRtl}
                  hasDelete={hasDelete}
                  handelRowClick={handelRowClick}
                  handelRowUpdateClick={handelRowUpdateClick}
                  handelRowDeleteClick={handelRowDeleteClick}
                  handelRowCancel={handelRowCancel}
                  withIndex={withIndex}
                  onClick={onClick}
                  editable={editable}
                ></TableRow>
              );

              // Only rows with a destination are links; an empty `to` would navigate to the
              // current path and drop its query string (e.g. the month on month reports)
              if (!clickable) {
                return <div key={index}>{row}</div>;
              }
              return (
                <div key={index}>
                  {to ? (
                    <RowLink to={{ ...to }}>{row}</RowLink>
                  ) : (
                    <ClickableRow>{row}</ClickableRow>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default inject()(observer(Table));
